import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CssBaseline,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  CalendarToday,
  Person,
  Business,
  Notifications,
  MeetingRoom,
} from "@mui/icons-material";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import getLPTheme from "./getLPTheme";
import { fetchUser } from "../apis/UserAPI";
import { getInitials } from "../utils/getInitials";
import { hashCode, generateColor } from "../utils/colorUtils";

// Helper function to get auth token from cookies (same as Profile.js)
const getAuthToken = () => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("_auth="))
    ?.split("=")[1];
  return token;
};

/**
 * UserDashboard - Authenticated user home page dashboard
 *
 * Displays a comprehensive dashboard with user information, quick actions,
 * upcoming bookings, company details, and recent activity for logged-in users.
 *
 * @component
 * @example
 * <UserDashboard />
 */
export default function UserDashboard() {
  // All React hooks must be called at the top level, before any early returns
  const [mode, setMode] = useState("light");
  const [userData, setUserData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const authUser = useAuthUser();
  const navigate = useNavigate();
  const theme = createTheme(getLPTheme(mode));

  const apiUrl = process.env.REACT_APP_API_URL;

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  /**
   * Fetch user details from the API using the same pattern as Profile.js
   */
  const fetchUserDetails = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setError("Authentication token not found. Please log in again.");
        return;
      }

      const data = await fetchUser(authUser?.uid, token);
      if (data && data.length > 0) {
        setUserData(data[0]);
        return data[0];
      } else {
        throw new Error("User data not found or invalid format.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setError(error.message || "Failed to load user data");
      return null;
    }
  }, [authUser?.uid]); // Use optional chaining for safety

  /**
   * Fetch company details from the API using userData.tid
   */
  const fetchCompanyDetails = useCallback(
    async (userData) => {
      try {
        if (!userData || !userData.tid) {
          return;
        }

        const token = getAuthToken();
        if (!token) {
          setError("Authentication token not found. Please log in again.");
          return;
        }

        // Fetch company details
        const detailsResponse = await axios.get(
          `${apiUrl}/api/get-company-details?companyId=${userData.tid}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        if (detailsResponse.data && detailsResponse.data.success) {
          setCompanyDetails(detailsResponse.data.data);
        }

        // Fetch company users/team members
        const usersResponse = await axios.get(
          `${apiUrl}/api/get-company-users?companyId=${userData.tid}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        if (
          usersResponse.data &&
          usersResponse.data.success &&
          Array.isArray(usersResponse.data.data)
        ) {
          // Filter out the current user from the team members list
          setCompanyUsers(
            usersResponse.data.data.filter((user) => user.uid !== authUser?.uid)
          );
        }
      } catch (error) {
        console.error("Error fetching company details:", error);
        // Don't set error for company details as it's not critical
      }
    },
    [apiUrl, authUser?.uid]
  );

  /**
   * Fetch upcoming bookings for the user using same pattern as Profile.js
   */
  const fetchUpcomingBookings = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setError("Authentication token not found. Please log in again.");
        return;
      }

      const response = await axios.get(
        `${apiUrl}/api/get-events-list?myEvents=true&limit=5`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      // Handle response structure similar to Profile.js
      if (response.data && Array.isArray(response.data.data)) {
        // API already returns events sorted by start date DESC
        // Filter for future events only and take first 5
        const upcomingEvents = response.data.data
          .filter((event) => new Date(event.start) > new Date()) // Only future events
          .slice(0, 5); // Limit to 5 upcoming events
        setUpcomingBookings(upcomingEvents);
      } else if (response.data && Array.isArray(response.data)) {
        // Alternative response structure - API already sorted
        const upcomingEvents = response.data
          .filter((event) => new Date(event.start) > new Date())
          .slice(0, 5);
        setUpcomingBookings(upcomingEvents);
      } else {
        console.warn(
          "API response for events is not in the expected format:",
          response.data
        );
        setUpcomingBookings([]);
      }
    } catch (error) {
      console.error("Error fetching upcoming bookings:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session may have expired. Please log in again.");
      }
      setUpcomingBookings([]);
    }
  }, [apiUrl]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError("");

      if (!authUser?.uid) {
        setError("User not authenticated.");
        setLoading(false);
        return;
      }

      try {
        // Fetch user data first
        const userDataResult = await fetchUserDetails();

        // Then fetch other data in parallel
        await Promise.all([
          fetchCompanyDetails(userDataResult),
          fetchUpcomingBookings(),
        ]);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [
    authUser?.uid,
    fetchUserDetails,
    fetchCompanyDetails,
    fetchUpcomingBookings,
  ]);

  // Now handle authentication check after all hooks
  if (!authUser) {
    return null; // Return null so HomePage component can handle routing properly
  }

  /**
   * Generate user initials for avatar
   */
  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Format date for booking display
   */
  const formatBookingDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  /**
   * Format time for booking display
   */
  const formatBookingTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /**
   * Map user type ID to display label based on Type enum from backend
   * @param {number} typeId - The user's type ID
   * @returns {string} - Human readable role label
   */
  const getUserRoleLabel = (typeId) => {
    switch (typeId) {
      case 1:
        return "Company User";
      case 2:
        return "Company Admin";
      case 3:
        return "Employee";
      case 4:
        return "Admin";
      default:
        return "User";
    }
  };

  /**
   * Get role chip color based on user type
   * @param {number} typeId - The user's type ID
   * @returns {object} - Style object for the chip
   */
  const getRoleChipStyle = (typeId) => {
    switch (typeId) {
      case 4: // Admin
        return {
          bgcolor: "#dc2626", // Red for admin
          color: "white",
          fontWeight: "bold",
        };
      case 3: // Employee
        return {
          bgcolor: "#2563eb", // Blue for employee
          color: "white",
          fontWeight: "bold",
        };
      case 2: // Company Admin
        return {
          bgcolor: "#7c3aed", // Purple for company admin
          color: "white",
          fontWeight: "bold",
        };
      case 1: // Company User
      default:
        return {
          bgcolor: "black",
          color: "white",
          fontWeight: "bold",
        };
    }
  };

  /**
   * Check if user has admin privileges (Company Admin or higher)
   * @param {number} typeId - The user's type ID
   * @returns {boolean} - True if user has admin privileges
   */
  const hasAdminPrivileges = (typeId) => {
    return typeId >= 2; // Company Admin (2), Employee (3), or Admin (4)
  };

  /**
   * Format credit balance for display
   * @param {number} credits - The credit amount
   * @returns {string} - Formatted credit string
   */
  const formatCredits = (credits) => {
    if (credits === null || credits === undefined) return "N/A";
    return credits.toLocaleString();
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppAppBar
          mode={mode}
          toggleColorMode={toggleColorMode}
        />
        <Box
          sx={{
            backgroundImage:
              "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
            backgroundSize: "100% 200px",
            backgroundRepeat: "no-repeat",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <CircularProgress />
          <Typography
            variant='h6'
            sx={{ mt: 2 }}
          >
            Loading dashboard...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppAppBar
          mode={mode}
          toggleColorMode={toggleColorMode}
        />
        <Container
          maxWidth='lg'
          sx={{ pt: { xs: 14, sm: 20 } }}
        >
          <Alert
            severity='error'
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
          <Button
            variant='contained'
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
      />
      <Box
        sx={{
          backgroundImage:
            "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
          backgroundSize: "100% 200px",
          backgroundRepeat: "no-repeat",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
          px: 3,
        }}
      >
        <Container
          maxWidth='lg'
          sx={{ pb: 4 }}
        >
          {/* Welcome Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant='h3'
              component='h1'
              gutterBottom
            >
              Welcome back, {userData?.name || authUser?.name}!
            </Typography>
            <Typography
              variant='h6'
              color='text.secondary'
            >
              Here's what's happening with your workspace today
            </Typography>
          </Box>

          <Grid
            container
            spacing={3}
          >
            {/* Quick Actions */}
            <Grid
              item
              xs={12}
              md={4}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant='h6'
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        bgcolor: "primary.main",
                        borderRadius: "50%",
                      }}
                    />
                    Quick Actions
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Button
                      variant='contained'
                      fullWidth
                      startIcon={<MeetingRoom />}
                      onClick={() => navigate("/rooms")}
                      sx={{
                        justifyContent: "flex-start",
                        bgcolor: "#cbdf0f",
                        color: "black",
                        "&:hover": { bgcolor: "#b8cc0d" },
                      }}
                    >
                      Book a Room
                    </Button>
                    <Button
                      variant='outlined'
                      fullWidth
                      startIcon={<CalendarToday />}
                      onClick={() => navigate("/my-calendar")}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      View My Calendar
                    </Button>
                    <Button
                      variant='outlined'
                      fullWidth
                      startIcon={<Person />}
                      onClick={() => navigate("/profile")}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      Edit Profile
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Profile Summary */}
            <Grid
              item
              xs={12}
              md={8}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant='h6'
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Person />
                    Profile Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "#4ade80",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                      }}
                    >
                      {getUserInitials(userData?.name || authUser?.name)}
                    </Avatar>
                    <Box>
                      <Typography variant='h6'>
                        {userData?.name || authUser?.name}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                      >
                        {userData?.contact || authUser?.email}
                      </Typography>
                      <Chip
                        label={getUserRoleLabel(
                          userData?.user_type || authUser?.typeId
                        )}
                        size='small'
                        sx={{
                          ...getRoleChipStyle(
                            userData?.user_type || authUser?.typeId
                          ),
                          mt: 1,
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Upcoming Bookings */}
            <Grid
              item
              xs={12}
              md={6}
            >
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant='h6'
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <CalendarToday />
                    Upcoming Bookings
                  </Typography>
                  {upcomingBookings.length > 0 ? (
                    <List dense>
                      {upcomingBookings.map((booking, index) => (
                        <React.Fragment
                          key={booking._id || booking.id || index}
                        >
                          <ListItem sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Typography
                                  variant='subtitle2'
                                  fontWeight='bold'
                                >
                                  {booking.title ||
                                    booking.description ||
                                    "Meeting"}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  {formatBookingDate(booking.start)} at{" "}
                                  {formatBookingTime(booking.start)} -{" "}
                                  {formatBookingTime(booking.end)}
                                </Typography>
                              }
                            />
                            {booking.roomName && (
                              <Chip
                                label={booking.roomName}
                                size='small'
                                sx={{
                                  ml: 1,
                                  bgcolor: "black",
                                  color: "white",
                                  fontSize: "0.75rem",
                                }}
                              />
                            )}
                          </ListItem>
                          {index < upcomingBookings.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 2 }}
                    >
                      No upcoming bookings
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Company Information */}
            <Grid
              item
              xs={12}
              md={6}
            >
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography
                    variant='h6'
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Business />
                    Company Information
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant='subtitle1'
                      fontWeight='bold'
                    >
                      {companyDetails?.name || "Your Company"}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 1, mb: 3 }}
                    >
                      {companyDetails?.description ||
                        "No description available"}
                    </Typography>

                    {/* Company Admin Information - Only for Company Admin or higher */}
                    {hasAdminPrivileges(
                      userData?.user_type || authUser?.typeId
                    ) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mb: 1 }}
                        >
                          <strong>Meeting Credit Balance:</strong>{" "}
                          {formatCredits(companyDetails?.meeting_cred)} Credits
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                        >
                          <strong>Registration Code:</strong>{" "}
                          {companyDetails?.reg_code || "N/A"}
                        </Typography>
                      </Box>
                    )}

                    {/* Team Members Section */}
                    <Typography
                      variant='subtitle2'
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      Team Members
                    </Typography>
                    {companyUsers.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {(() => {
                          // Calculate color once outside the map if companyDetails exist
                          const companyColor = companyDetails?.tid
                            ? generateColor(
                                hashCode(companyDetails.tid.toString())
                              )
                            : "#808080"; // Default grey

                          return companyUsers.map((user) => (
                            <Tooltip
                              title={user.name || "User"}
                              key={user.uid}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: companyColor, // Apply generated color
                                  color: "#ffffff", // Ensure text is readable (white)
                                  width: 32,
                                  height: 32,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {user.name ? (
                                  getInitials(user.name)
                                ) : (
                                  <Person fontSize='small' />
                                )}
                              </Avatar>
                            </Tooltip>
                          ));
                        })()}
                      </Box>
                    ) : (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                      >
                        No other team members found.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant='h6'
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Notifications />
                    Recent Activity
                  </Typography>
                  <Box sx={{ mt: 2, textAlign: "center", py: 4 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      No recent activity to display
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </ThemeProvider>
  );
}
